import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import NutritionTargets from './NutritionTargets';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

interface ProductionMetricsData {
  mealsToday?: number;
  onTimeRate?: number;
  qualityScore?: number;
  staffActive?: number;
}

interface KitchenAlert {
  id?: string | number;
  message?: string;
  description?: string;
  time?: string;
  timestamp?: string;
}

interface KitchenProfile {
  id: string;
  kitchenName?: string;
  kitchenCode?: string;
  name?: string;
  type?: string;
}

type SubmitQuickActionDialog = 'broadcast' | 'inspection' | 'capacity' | 'emergency';
type QuickActionDialog = SubmitQuickActionDialog | 'nutrition' | null;

const defaultActionForm = {
  message: '',
  audience: 'all-kitchens',
  priority: 'normal',
  kitchenId: '',
  inspectionType: 'quality-safety',
  scheduledAt: '',
  assignedTo: '',
  notes: '',
  scope: 'all-kitchens',
  adjustmentType: 'temporary',
  targetCapacity: '',
  capacityDelta: '',
  effectiveFrom: '',
  reason: '',
  severity: 'critical',
  incidentType: 'operations-disruption',
};

const ProductionMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<ProductionMetricsData | null>(null);
  const [alerts, setAlerts] = useState<KitchenAlert[]>([]);
  const [kitchens, setKitchens] = useState<KitchenProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] = useState<QuickActionDialog>(null);
  const [actionForm, setActionForm] = useState(defaultActionForm);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const headers = authHeaders();

      const [metricsResp, alertsResp, kitchensResp] = await Promise.allSettled([
        fetch(`${API_BASE}/kitchen-ops/metrics`, { headers }),
        fetch(`${API_BASE}/kitchen-ops/alerts`, { headers }),
        fetch(`${API_BASE}/kitchen-ops/kitchens`, { headers }),
      ]);

      if (metricsResp.status === 'fulfilled' && metricsResp.value.ok) {
        const data = await metricsResp.value.json();
        setMetrics(data.data ?? data);
      }

      if (alertsResp.status === 'fulfilled' && alertsResp.value.ok) {
        const data = await alertsResp.value.json();
        const list = data.data ?? data;
        setAlerts(Array.isArray(list) ? list : []);
      }

      if (kitchensResp.status === 'fulfilled' && kitchensResp.value.ok) {
        const data = await kitchensResp.value.json();
        const list = data.data ?? data;
        setKitchens(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      setError((e as Error).message || 'Failed to load production metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const openDialog = (dialog: QuickActionDialog): void => {
    setActionForm({
      ...defaultActionForm,
      scheduledAt: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
      effectiveFrom: new Date().toISOString().slice(0, 16),
    });
    setActiveDialog(dialog);
  };

  const closeDialog = (): void => {
    if (!submittingAction) setActiveDialog(null);
  };

  const updateActionForm = (field: keyof typeof defaultActionForm, value: string): void => {
    setActionForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitQuickAction = async (): Promise<void> => {
    if (!activeDialog || activeDialog === 'nutrition') return;

    const endpointMap: Record<SubmitQuickActionDialog, string> = {
      broadcast: 'broadcast',
      inspection: 'inspections',
      capacity: 'capacity-adjustments',
      emergency: 'emergency-protocol',
    };

    const payloadMap: Record<SubmitQuickActionDialog, any> = {
      broadcast: {
        message: actionForm.message,
        audience: actionForm.audience,
        priority: actionForm.priority,
      },
      inspection: {
        kitchenId: actionForm.kitchenId || null,
        inspectionType: actionForm.inspectionType,
        scheduledAt: actionForm.scheduledAt ? new Date(actionForm.scheduledAt).toISOString() : undefined,
        assignedTo: actionForm.assignedTo,
        notes: actionForm.notes,
      },
      capacity: {
        scope: actionForm.scope,
        kitchenId: actionForm.kitchenId || null,
        adjustmentType: actionForm.adjustmentType,
        targetCapacity: actionForm.targetCapacity ? Number(actionForm.targetCapacity) : undefined,
        capacityDelta: actionForm.capacityDelta ? Number(actionForm.capacityDelta) : undefined,
        effectiveFrom: actionForm.effectiveFrom ? new Date(actionForm.effectiveFrom).toISOString() : undefined,
        reason: actionForm.reason,
      },
      emergency: {
        severity: actionForm.severity,
        incidentType: actionForm.incidentType,
        message: actionForm.message,
        actions: ['notify-kitchen-managers', 'activate-backup-suppliers', 'dispatch-response-teams'],
      },
    };

    setSubmittingAction(true);
    try {
      const resp = await fetch(`${API_BASE}/kitchen-ops/${endpointMap[activeDialog]}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payloadMap[activeDialog]),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || `Action failed: ${resp.status}`);

      setSnackbar({ open: true, message: 'Quick action submitted to Kitchen Ops API', severity: 'success' });
      setActiveDialog(null);
      await load();
    } catch (err: unknown) {
      setSnackbar({ open: true, message: (err as Error).message || 'Quick action failed', severity: 'error' });
    } finally {
      setSubmittingAction(false);
    }
  };

  const isSubmitDisabled = (): boolean => {
    if (submittingAction) return true;
    if (activeDialog === 'broadcast') return !actionForm.message.trim();
    if (activeDialog === 'inspection') return !actionForm.scheduledAt;
    if (activeDialog === 'capacity') return !actionForm.reason.trim() || (!actionForm.targetCapacity && !actionForm.capacityDelta);
    if (activeDialog === 'emergency') return !actionForm.message.trim();
    return false;
  };

  const metricCardStyle = {
    background: 'linear-gradient(135deg, #28a745, #20c997)',
    color: 'white',
    p: 2.5,
    borderRadius: 2,
    textAlign: 'center',
  };

  const blueButtonStyle = {
    background: 'linear-gradient(135deg, #007bff, #0056b3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0056b3, #004085)',
      transform: 'translateY(-2px)',
      boxShadow: '0 5px 15px rgba(0,123,255,0.4)',
    },
    transition: 'all 0.3s ease',
  };

  return (
    <Card sx={{ height: '100%', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', borderRadius: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Production Metrics
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {metrics ? (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={metricCardStyle}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {metrics.mealsToday != null ? `${(metrics.mealsToday / 1000).toFixed(0)}K` : '--'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Meals Today</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={metricCardStyle}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {metrics.onTimeRate != null ? `${metrics.onTimeRate}%` : '--'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>On-Time Rate</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={metricCardStyle}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {metrics.qualityScore != null ? `${metrics.qualityScore}%` : '--'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Quality Score</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={metricCardStyle}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {metrics.staffActive != null ? metrics.staffActive.toLocaleString() : '--'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Staff Active</Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                No production metrics available from the API.
              </Alert>
            )}

            <Typography
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <WarningIcon color="warning" />
              Active Alerts
            </Typography>
            {alerts.length === 0 ? (
              <Alert severity="success" sx={{ mb: 3 }}>No active alerts.</Alert>
            ) : (
              <Box
                sx={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                }}
              >
                {alerts.map((alert, index) => (
                  <Box
                    key={alert.id ?? index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: index < alerts.length - 1 ? '1px solid #ffeaa7' : 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#856404', flex: 1 }}>
                      {alert.message ?? alert.description ?? ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#856404', ml: 2 }}>
                      {alert.time ?? (alert.timestamp ? new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'medium' }}>
          Quick Controls
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="contained" startIcon={<CampaignIcon />} onClick={() => openDialog('broadcast')} sx={blueButtonStyle}>
              Broadcast Message
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={() => openDialog('inspection')} sx={blueButtonStyle}>
              Schedule Inspection
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="contained" startIcon={<SettingsIcon />} onClick={() => openDialog('capacity')} sx={blueButtonStyle}>
              Adjust Capacity
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="contained" startIcon={<MenuBookIcon />} onClick={() => openDialog('nutrition')} sx={blueButtonStyle}>
              Nutrition Targets
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<WarningIcon />}
              onClick={() => openDialog('emergency')}
              sx={{
                background: 'linear-gradient(135deg, #dc3545, #c82333)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c82333, #bd2130)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(220,53,69,0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Emergency Protocol
            </Button>
          </Grid>
        </Grid>

        <Dialog open={activeDialog === 'broadcast'} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Broadcast Message</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField fullWidth multiline minRows={3} label="Message" value={actionForm.message} onChange={(event) => updateActionForm('message', event.target.value)} />
              <FormControl fullWidth>
                <InputLabel id="broadcast-audience-label">Audience</InputLabel>
                <Select labelId="broadcast-audience-label" label="Audience" value={actionForm.audience} onChange={(event) => updateActionForm('audience', event.target.value)}>
                  <MenuItem value="all-kitchens">All kitchens</MenuItem>
                  <MenuItem value="central-kitchens">Central kitchens</MenuItem>
                  <MenuItem value="satellite-kitchens">Satellite kitchens</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="broadcast-priority-label">Priority</InputLabel>
                <Select labelId="broadcast-priority-label" label="Priority" value={actionForm.priority} onChange={(event) => updateActionForm('priority', event.target.value)}>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" onClick={submitQuickAction} disabled={isSubmitDisabled()}>
              Send Broadcast
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'inspection'} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Schedule Inspection</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="inspection-kitchen-label">Kitchen</InputLabel>
                <Select labelId="inspection-kitchen-label" label="Kitchen" value={actionForm.kitchenId} onChange={(event) => updateActionForm('kitchenId', event.target.value)}>
                  <MenuItem value="">All kitchens</MenuItem>
                  {kitchens.map((kitchen) => (
                    <MenuItem key={kitchen.id} value={kitchen.id}>
                      {kitchen.kitchenName || kitchen.name} {kitchen.kitchenCode ? `(${kitchen.kitchenCode})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="inspection-type-label">Inspection Type</InputLabel>
                <Select labelId="inspection-type-label" label="Inspection Type" value={actionForm.inspectionType} onChange={(event) => updateActionForm('inspectionType', event.target.value)}>
                  <MenuItem value="quality-safety">Quality and safety</MenuItem>
                  <MenuItem value="food-safety">Food safety</MenuItem>
                  <MenuItem value="warehouse">Warehouse/store</MenuItem>
                  <MenuItem value="distribution">Distribution readiness</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth type="datetime-local" label="Scheduled time" InputLabelProps={{ shrink: true }} value={actionForm.scheduledAt} onChange={(event) => updateActionForm('scheduledAt', event.target.value)} />
              <TextField fullWidth label="Assigned to" value={actionForm.assignedTo} onChange={(event) => updateActionForm('assignedTo', event.target.value)} />
              <TextField fullWidth multiline minRows={2} label="Notes" value={actionForm.notes} onChange={(event) => updateActionForm('notes', event.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" onClick={submitQuickAction} disabled={isSubmitDisabled()}>
              Schedule
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'capacity'} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Adjust Capacity</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="capacity-scope-label">Scope</InputLabel>
                <Select labelId="capacity-scope-label" label="Scope" value={actionForm.scope} onChange={(event) => updateActionForm('scope', event.target.value)}>
                  <MenuItem value="all-kitchens">All kitchens</MenuItem>
                  <MenuItem value="single-kitchen">Single kitchen</MenuItem>
                  <MenuItem value="satellite-kitchens">Satellite kitchens</MenuItem>
                </Select>
              </FormControl>
              {actionForm.scope === 'single-kitchen' && (
                <FormControl fullWidth>
                  <InputLabel id="capacity-kitchen-label">Kitchen</InputLabel>
                  <Select labelId="capacity-kitchen-label" label="Kitchen" value={actionForm.kitchenId} onChange={(event) => updateActionForm('kitchenId', event.target.value)}>
                    {kitchens.map((kitchen) => (
                      <MenuItem key={kitchen.id} value={kitchen.id}>
                        {kitchen.kitchenName || kitchen.name} {kitchen.kitchenCode ? `(${kitchen.kitchenCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth>
                <InputLabel id="capacity-type-label">Adjustment Type</InputLabel>
                <Select labelId="capacity-type-label" label="Adjustment Type" value={actionForm.adjustmentType} onChange={(event) => updateActionForm('adjustmentType', event.target.value)}>
                  <MenuItem value="temporary">Temporary</MenuItem>
                  <MenuItem value="shift-extension">Shift extension</MenuItem>
                  <MenuItem value="daily-override">Daily override</MenuItem>
                </Select>
              </FormControl>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Target capacity" value={actionForm.targetCapacity} onChange={(event) => updateActionForm('targetCapacity', event.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="number" label="Capacity delta" value={actionForm.capacityDelta} onChange={(event) => updateActionForm('capacityDelta', event.target.value)} />
                </Grid>
              </Grid>
              <TextField fullWidth type="datetime-local" label="Effective from" InputLabelProps={{ shrink: true }} value={actionForm.effectiveFrom} onChange={(event) => updateActionForm('effectiveFrom', event.target.value)} />
              <TextField fullWidth multiline minRows={2} label="Reason" value={actionForm.reason} onChange={(event) => updateActionForm('reason', event.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="contained" onClick={submitQuickAction} disabled={isSubmitDisabled()}>
              Submit Adjustment
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'nutrition'} onClose={closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>Nutrition Targets</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <NutritionTargets />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={activeDialog === 'emergency'} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Emergency Protocol</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="error">
                This activates kitchen manager alerts, backup supplier escalation, and response team dispatch records.
              </Alert>
              <FormControl fullWidth>
                <InputLabel id="emergency-severity-label">Severity</InputLabel>
                <Select labelId="emergency-severity-label" label="Severity" value={actionForm.severity} onChange={(event) => updateActionForm('severity', event.target.value)}>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="major">Major</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="emergency-type-label">Incident Type</InputLabel>
                <Select labelId="emergency-type-label" label="Incident Type" value={actionForm.incidentType} onChange={(event) => updateActionForm('incidentType', event.target.value)}>
                  <MenuItem value="operations-disruption">Operations disruption</MenuItem>
                  <MenuItem value="food-safety">Food safety</MenuItem>
                  <MenuItem value="supplier-failure">Supplier failure</MenuItem>
                  <MenuItem value="distribution-failure">Distribution failure</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth multiline minRows={3} label="Incident message" value={actionForm.message} onChange={(event) => updateActionForm('message', event.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button color="error" variant="contained" onClick={submitQuickAction} disabled={isSubmitDisabled()}>
              Activate Protocol
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default ProductionMetrics;
